/* import __COLOCATED_TEMPLATE__ from './workflow-header.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import {
  AI_AGENT_WORKFLOW_TARGET,
  OPERATOR_CUSTOM_BOT_OVERVIEW_ROUTE,
} from 'embercom/lib/operator/custom-bots/constants';
import {
  OUTBOUND_EDITOR_ROUTES,
  OUTBOUND_INDEX_ROUTES,
  FIN_TASKS_ROUTES,
} from 'embercom/models/outbound/content-wrapper';
import type Router from '@ember/routing/router-service';
import type Tag from 'embercom/objects/inbox/tag';
import type WorkflowSidedrawerService from 'embercom/services/workflow-sidedrawer-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type Store from '@ember-data/store';
import type WorkflowLocalizationService from 'embercom/services/workflow-localization-service';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  config: any;
}

interface Signature {
  Args: Args;
}

export default class WorkflowHeader extends Component<Signature> {
  @service declare contentEditorService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare router: Router;
  @service declare workflowSidedrawerService: WorkflowSidedrawerService;
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;
  @service declare workflowLocalizationService: WorkflowLocalizationService;
  @service declare intl: IntlService;

  get hideMenuToggle(): boolean {
    return this.appService.app.canUseStandalone || this.contentEditorService.isOpenInFinStandalone;
  }

  get hasWorkflowHeaderRedesign(): boolean {
    return this.appService.app.canUseWorkflowHeaderRedesign;
  }

  get isBackgroundWorkflow(): boolean {
    return this.contentEditorService.activeObject.visualBuilderObject?.isCustomerFacing === false;
  }

  get isSideDrawer(): boolean {
    return this.workflowSidedrawerService.sidedrawerOpen;
  }

  get isInStandaloneSection() {
    return this.router.currentRouteName.includes('apps.app.standalone');
  }

  get allowPreview(): boolean {
    if (this.isInStandaloneSection) {
      return this.contentEditorService.activeObject?.target === AI_AGENT_WORKFLOW_TARGET;
    }

    return (
      !this.contentEditorService.activeObject.visualBuilderObject?.isFinPreview &&
      !this.isInStandaloneSection
    );
  }

  get showStateChangeButtons() {
    if (this.isInStandaloneSection) {
      return (
        this.contentEditorService.activeObject?.target === AI_AGENT_WORKFLOW_TARGET ||
        this.appService.app.canUseMultipleWorkflows
      );
    } else {
      return true;
    }
  }

  get isInStandaloneTasksSection() {
    return this.router.currentRouteName.includes('apps.app.standalone.tasks.workflow');
  }

  get shouldDisablePauseButton() {
    return this.contentEditorService.activeObject?.canUpdateState === false;
  }

  get isInStandaloneWorkflowsSection() {
    return this.isInStandaloneSection && !this.isInStandaloneTasksSection;
  }

  @action
  editWorkflow() {
    this.workflowSidedrawerService.editWorkflow();
  }

  @action
  async removeTag(tag: Tag) {
    await this.contentEditorService.ruleset.updateTaggingsTask.perform([], [tag]);
    this.contentEditorService.ruleset.reload();
  }

  @action
  async close() {
    if (this.contentEditorService.returnPath) {
      this.router.transitionTo(this.contentEditorService.returnPath);
    } else if (this.isInStandaloneSection) {
      this.router.transitionTo(this.finStandaloneService.workflowsReturnRoute);
    } else if (this.isSideDrawer) {
      this.workflowSidedrawerService.teardown();
    } else if (this.router.currentRouteName === FIN_TASKS_ROUTES.task) {
      this.router.transitionTo(FIN_TASKS_ROUTES.tasks);
    } else {
      this.router.currentRouteName === OUTBOUND_EDITOR_ROUTES['custom_bot']
        ? this.router.transitionTo(OUTBOUND_INDEX_ROUTES['custom_bot'])
        : this.router.transitionTo(OPERATOR_CUSTOM_BOT_OVERVIEW_ROUTE);
    }
  }

  get hasDraftWorkflowDifferentFromLive() {
    if (!this.appService.app.canUseFeature('workflow-draft-state')) {
      return false;
    }

    let customBot = this.contentEditorService.activeObject;

    if (!customBot.draftVisualBuilderObject || !customBot.visualBuilderObject) {
      return false;
    }
    return customBot.draftVisualBuilderObject.id !== customBot.visualBuilderObject.id;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentEditor::Headers::ViewMode::WorkflowHeader': typeof WorkflowHeader;
  }
}
